import React, { useState, useEffect } from 'react';
import { MessageCircle, X } from 'lucide-react';

// Floating WhatsApp help button. Appears on every screen and opens a chat
// with the CuminJar support business number. Sits above the mobile bottom
// nav so it never overlaps the record FAB.
const SUPPORT_NUMBER = '918122217689'; // +91 8122 217 689
const DEFAULT_MSG = "Hi CuminJar team! I need help with my account.";

export default function WhatsAppSupport() {
  const [showTip, setShowTip] = useState(false);
  const [dismissedTip, setDismissedTip] = useState(true);

  // Show a small "Need help? Chat with us" tooltip once per session so users
  // notice the button, then let it stay hidden until they click.
  useEffect(() => {
    let cancelled = false;
    try {
      if (sessionStorage.getItem('cuminjar_wa_support_tip_seen')) return;
      setDismissedTip(false);
      const t = setTimeout(() => { if (!cancelled) setShowTip(true); }, 4000);
      const t2 = setTimeout(() => { if (!cancelled) { setShowTip(false); sessionStorage.setItem('cuminjar_wa_support_tip_seen', '1'); } }, 12000);
      return () => { cancelled = true; clearTimeout(t); clearTimeout(t2); };
    } catch { /* privacy mode — skip */ }
  }, []);

  const dismissTip = (e) => {
    e.stopPropagation();
    setShowTip(false);
    setDismissedTip(true);
    try { sessionStorage.setItem('cuminjar_wa_support_tip_seen', '1'); } catch { /* ignore */ }
  };

  const openChat = () => {
    setShowTip(false);
    setDismissedTip(true);
    try { sessionStorage.setItem('cuminjar_wa_support_tip_seen', '1'); } catch { /* ignore */ }
    const url = `https://wa.me/${SUPPORT_NUMBER}?text=${encodeURIComponent(DEFAULT_MSG)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div
      className="fixed z-40 right-4 bottom-20 lg:right-6 lg:bottom-6 flex items-end gap-2"
      data-testid="whatsapp-support-fab"
    >
      {showTip && !dismissedTip && (
        <div
          className="hidden sm:flex flex-col items-stretch bg-white border border-neutral-200 rounded-2xl shadow-md pl-3 pr-2 py-2 animate-fade-in-up max-w-[240px]"
          data-testid="whatsapp-support-tip"
        >
          <div className="flex items-center gap-2">
            <span className="text-[12.5px] font-medium text-neutral-800 flex-1">Need help? Chat with us</span>
            <button
              type="button"
              onClick={dismissTip}
              aria-label="Dismiss"
              className="w-5 h-5 rounded-full flex items-center justify-center text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700 transition-colors flex-shrink-0"
            >
              <X size={12} />
            </button>
          </div>
          <p className="mt-0.5 text-[10.5px] text-neutral-500 leading-snug pr-6">
            Usually replies within 30 min &middot; Mon&ndash;Sat 10am&ndash;7pm IST
          </p>
        </div>
      )}

      <button
        type="button"
        onClick={openChat}
        aria-label="Chat with CuminJar support on WhatsApp"
        title="Chat with CuminJar support on WhatsApp"
        data-testid="whatsapp-support-btn"
        className="group w-13 h-13 sm:w-14 sm:h-14 rounded-full bg-[#25D366] hover:bg-[#1EBE5B] text-white shadow-lg hover:shadow-xl transition-all flex items-center justify-center relative"
        style={{ width: 52, height: 52 }}
      >
        <MessageCircle size={24} className="drop-shadow" fill="currentColor" fillOpacity={0.15} />
        <span className="absolute inset-0 rounded-full bg-[#25D366] opacity-40 animate-ping pointer-events-none" aria-hidden="true" />
      </button>
    </div>
  );
}
